import { patchPersona, track } from '@sloyd/analytics';
import { actions, selectors } from '@sloyd/store-base';
import { prompt, toast } from '@sloyd/ui';
import { delay, fork, put, select, takeEvery } from 'saga-ts';
import { $a, guid4, invokeEvent, l } from 'shared-base';
import { formDefaults, forms } from '../_definitions/forms';
import { getRandomEmoji } from '../utils/emojies';
import { dataGuard } from './helpers/guards';
import { navToModel, navToNewModel, navToProject } from './helpers/navigation';

export const saga = {
  id: 'sloyd.projects',
  type: 'entity',
  root: root,
  trigger: {
    actionTypes: ['PROJECTS'],
  },
};

type Verb =
  | 'new' //
  | 'create' //
  | 'load'
  | 'open'
  | 'emoji'
  | 'rename'
  | 'clear'
  | 'delete';

type Action = {
  type: 'PROJECTS';
  verb: Verb;
  id: string;
  payload: Json;
};

const map: Record<Verb, any> = {
  new: newProject,
  create: createProject,
  load: loadProject,
  delete: deleteProject,
  open: openProject,
  rename: renameProject,
  clear: clearProject,
  emoji: emoji,
};

const analyitcsMapProjects: any = {
  new: 'New Project',
  create: 'Create Project',
  load: 'Load Project',
  delete: 'Delete Project',
  open: 'Open Project',
  rename: 'Rename Project',
  clear: 'Clear Project',
  emoji: 'Change Project Emoji',
};

export function* openProject(action: Action, project: IProject) {
  try {
    if (!project) return;
    const { id } = project;
    yield navToProject(id);
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* clearProject(action: Action, project: IProject) {
  if (!project) return;
  yield put($a('CLEAR_PROJECT', '', project.id));
}

export function* renameProject(action: Action, project: IProject) {
  try {
    if (!project) return;
    const { id, name } = project;

    const { value, didCancel } = yield prompt.input({
      title: 'Rename Project',
      defaultValue: name,
      ctaButtonText: 'Rename',
    });

    if (didCancel) {
      return;
    }

    yield put(actions.projects.patch(id, { name: value }));
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* emoji(action: Action, project: IProject) {
  try {
    if (!project) return;
    const { id } = project;

    const { didCancel, value } = yield prompt.emoji({
      title: 'Select an emoji',
    });

    if (didCancel) {
      return;
    }

    yield put(actions.projects.patch(id, { emoji: value.emoji }));
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* newProject(action: Action) {
  try {
    const isEmployee = yield* select(selectors.base.$isEmployee);

    const { value, didCancel } = yield prompt.form({
      title: 'New Project',
      form: {
        // config: isEmployee ? forms.newProjectWithScene : forms.newProject,
        config: forms.newProject,
        allDetails: {},
        allMethods: {},
        allOptions: {},
        data: formDefaults.newProject,
      },
    });

    if (didCancel || !value) {
      return;
    }

    const { projectName, projectType } = value;

    const emoji = getRandomEmoji();

    yield put(
      $a('PROJECTS', 'create', action.id, {
        name: projectName,
        type: projectType,
        emoji,
      })
    );
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* createProject(action: Action) {
  try {
    const { payload } = action;

    if (!payload.name) {
      return;
    }

    const user = yield* select(selectors.base.$user);

    const guid = action.id || guid4();

    const project = {
      id: guid,
      thumbnail: '',
      type: 'asset-pack',
      modifiedAt: new Date().toISOString(),
      emoji: '🆕',
      isPublic: user.isEmployee,
      ...payload,
    };

    yield put(actions.projects.add(project));
    yield put(actions.currentIds.patch({ projectId: guid }));

    const projects = yield* select(selectors.base.$projects);
    const count = Object.keys(projects).length;
    patchPersona({ projectsCount: count });
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* deleteProject(action: Action, project: IProject) {
  try {
    if (!project) return;
    const { id, name } = project;

    const { didCancel } = yield prompt.confirm({
      title: 'Delete Project',
      description: `Are you sure you want to delete "${name}"?`,
      ctaButtonText: 'Delete',
    });

    if (didCancel) {
      return;
    }

    yield put(actions.projects.delete(id));
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* loadProject(action: Action, project: IProject) {
  try {
    if (!project) {
      toast.error('Error loading project');
      return;
    }

    const { models, type } = project;
    const editMode = type !== 'scene' ? 'object' : 'scene';

    yield put(
      actions.appState.patch({
        currentBarIndex: 1,
        editMode,
      })
    );

    yield put(actions.models.setAll(models));

    if (type === 'scene') {
      yield put(actions.sceneItems.setAll({}));
    }

    yield delay(500);

    const modelsCount = Object.keys(models ?? {}).length;

    switch (modelsCount) {
      case 0:
        if (type !== 'scene') {
          yield navToNewModel();
        }

        break;
      case 1:
        const modelId = Object.keys(models)[0];
        yield navToModel(modelId);
        break;
      default:
    }
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* projects(action: any) {
  const { verb, id, origin, payload } = action;
  const { name } = payload || {};

  yield delay(10);

  const saga = (map as any)[verb];

  if (!saga) {
    return;
  }

  l(`saga|saga.projects ${verb} on ${id}`);

  yield* dataGuard();

  const project = yield* select(selectors.single.$project, id);

  track(analyitcsMapProjects[verb], { origin, id, name });

  yield* fork(saga, action, project);
}

export function* root() {
  yield takeEvery('PROJECTS', projects);
}

/*
  saga uses generators

  call => calls a function
  fork => forks a generator (runs it in parallel)
  put => dispatches an action
  select => gets the state
  takeEvery => listens for an action
  takeLatest => listens for an action and cancels the previous one
  delay => delays the execution of the generator
*/
